﻿import { DateFormat, ds, ts } from '@/plugins/i18nFormat'
import { dateRange } from '@/utils/dateUtils'
import Club from '@model/Club'
import EventBoat from '@model/EventBoat'
import EventCategory from '@model/EventCategory'
import EventClass from '@model/EventClass'
import EventCourse from '@model/EventCourse'
import EventLifeguard from '@model/EventLifeguard'
import EventRace from '@model/EventRace'
import EventReferee from '@model/EventReferee'
import EventResult from "@model/EventResult"
import Member from '@model/Member'
import NoticeboardEntry from '@model/NoticeboardEntry'
import Venue from '@model/Venue'
import EventsRepo from '@repo/EventsRepo'
import { Model, useRepo } from 'pinia-orm'
import { DateCast } from 'pinia-orm/casts'
import { Attr, BelongsTo, Bool, Cast, HasMany, Num, OnDelete, Str } from 'pinia-orm/decorators'

export enum ResultsEnterModel {
    EnterNumByNum,
    EnterDirectClick,
    EnterCountRounds,
    EnterBigField
}

export default class Event extends Model {
    static entity = 'events'

    @Num( 0, { notNullable: true } ) declare id: number
    @Str( '', { notNullable: true } ) declare title: string
    @Num( 0, { notNullable: true } ) declare ctlId: number
    @Cast( () => DateCast ) @Attr( '' ) declare from: string
    @Cast( () => DateCast ) @Attr( null ) declare to: string | null
    @Num( null ) declare clubId: number | null
    @BelongsTo( () => Club, 'clubId' ) declare club: Club | null
    @Str( null ) declare organizer: string | null
    @Num( null ) declare venueId: number | null
    @BelongsTo( () => Venue, 'venueId' ) declare venue: Venue | null
    @Str( null ) declare otherVenue: string | null
    @Str( null ) declare registrationUrl: string | null
    @Str( null ) declare noticeboardUrl: string | null
    @Str( null ) declare description: string | null
    @Str( null ) declare sponsor: string | null
    @HasMany( () => EventClass, 'eventId' ) @OnDelete( 'cascade' ) declare classes: EventClass[]
    @Str( null ) declare directorId: string | null
    @BelongsTo( () => Member, 'directorId' ) declare directorMember: Member | null
    @Attr( null ) declare director: string | null
    @Str( null ) declare guarantorId: string | null
    @BelongsTo( () => Member, 'guarantorId' ) declare guarantorMember: Member | null
    @HasMany( () => EventReferee, 'eventId' ) @OnDelete( 'cascade' ) declare referees: EventReferee[]
    @HasMany( () => EventLifeguard, 'eventId' ) @OnDelete( 'cascade' ) declare lifeguards: EventLifeguard[]
    @HasMany( () => EventBoat, 'eventId' ) @OnDelete( 'cascade' ) declare boats: EventBoat[]
    @HasMany( () => EventRace, 'eventId' ) @OnDelete( 'cascade' ) declare races: EventRace[]
    @Num( null ) declare plannedRaces: number | null
    @Bool( false ) declare noLifeguards: boolean
    @Str( null ) declare rrsUuid: string | null
    @HasMany( () => EventCourse, 'eventId' ) @OnDelete( 'cascade' ) declare courses: EventCourse[]
    @Str( null ) declare descInResultsTitle: string | null
    @Str( null ) declare crewDescInResultsTitle: string | null
    @HasMany( () => NoticeboardEntry, 'eventId' ) @OnDelete( 'cascade' ) declare noticeboardEntries: NoticeboardEntry[]
    @Str( null ) declare wsEventId: string | null

    @Cast( () => DateCast ) @Attr( '' ) declare created: string
    @Cast( () => DateCast ) @Attr( '' ) declare lastOpened: string
    @Str( 'EventConfigurationTab' ) declare currentTab: string
    @Bool( false ) declare ignoreUpdateMembers: boolean
    @Bool( false ) declare showOnlyCaptains: boolean
    @Num( ResultsEnterModel.EnterNumByNum ) declare currentResultsEnterMode: number
    @Bool( false ) declare forceEnglishReports: boolean

    @Bool( false ) declare modified: boolean
    
    get isNationalChampionship (): boolean {
        return this.classes.some( cl => cl.isNationalChampionship )
    }

    get scorerRegIds (): string[] {
        return this.referees
            .filter( r => r.scorer && r.referee !== null && r.referee.regId !== null )
            .map( r => r.referee!.regId.replace( '-', '' ) )
    }

    get dump () {
        const current = useRepo( Event )
            .with( 'classes', ( query ) => {
                query.with( 'categories' )
                query.with( 'boatClass' )
            } )
            .with( 'referees', ( query ) => {
                query.with( 'course' )
            } )
            .with( 'lifeguards' )
            .with( 'boats', ( query ) => {
                query.with( 'crew' )
                query.with( 'manualCategories' )
                query.with( 'eventBoatRaces', ( qbr ) => {
                    qbr.with( 'eventRace' )
                } )
                query.with( 'boatClass' )
                query.with( 'event' )
            } )
            .with( 'races', ( query ) => {
                query.with( 'event', ( qe ) => {
                    qe.with( 'classes' )
                    qe.with( 'boats' )
                } )
                query.with( 'results', ( qr ) => {
                    qr.with( 'race', ( qrr ) => {
                        qrr.with( 'results', ( qrrr ) => {
                            qrrr.with( 'boat' )
                        } )
                        qrr.with( 'event', ( qe ) => {
                            qe.with( 'classes' )
                            qe.with( 'boats' )
                        } )
                    } )
                    qr.with( 'boat', ( qb ) => {
                        qb.with( 'event', ( qe ) => {
                            qe.with( 'classes' )
                            qe.with( 'boats' )
                        } )
                        qb.with( 'boatClass' )
                        qb.with( 'eventBoatRaces' )
                    } )
                } )
                query.with( 'rounds', ( qr ) => {
                    qr.with( 'boat' )
                } )
                query.with( 'heatResults', ( qh ) => {
                    qh.with( 'boat' )
                } )
                query.with( 'skippedClasses' )
                query.with( 'detailedClasses' )
            } )
            .with( 'courses', ( query ) => {
                query.with( 'objects' )
            } )
            .find( this.id )

        if ( current === null )
            return null

        return {
            ctl: current.ctlId,
            title: current.title,
            from: ds( current.from, DateFormat.SystemDate ),
            to: current.to === null ? null : ds( current.to, DateFormat.SystemDate ),
            clubId: current.clubId,
            organizer: current.organizer,
            venueId: current.venueId,
            otherVenue: current.otherVenue,
            registrationUrl: current.registrationUrl,
            noticeboardUrl: current.noticeboardUrl,
            description: current.description,
            sponsor: current.sponsor,
            directorId: current.directorId,
            director: current.director,
            guarantorId: current.guarantorId,
            plannedRaces: current.plannedRaces,
            created: ds( current.created, DateFormat.System ),
            lastOpened: ds( current.lastOpened, DateFormat.System ),
            ignoreUpdateMembers: current.ignoreUpdateMembers,
            noLifeguards: current.noLifeguards,
            rrsUuid: current.rrsUuid,
            wsEventId: current.wsEventId,
            descInResultsTitle: current.descInResultsTitle,
            crewDescInResultsTitle: current.crewDescInResultsTitle,
            forceEnglishReports: current.forceEnglishReports,
            classes: current.classes.map( cl => cl.dump ),
            referees: current.referees.map( referee => referee.dump ),
            lifeguards: current.lifeguards.map( lifeguard => lifeguard.dump ),
            boats: current.boats.map( boat => boat.dump ),
            races: current.races.map( race => race.dump ),
            courses: current.courses.map( course => course.dump )
        }
    }

    get directorName (): string {
        if ( this.directorId !== null && this.directorMember ) {
            return this.directorMember.reverseFullNameWithRegId
        }

        if ( Array.isArray( this.director ) ) {
            return this.director.join( ', ' )
        }

        return this.director ?? ''
    }

    get guarantorName (): string {
        if ( this.guarantorId !== null && this.guarantorMember !== null ) {
            return this.guarantorMember.reverseFullNameWithRegId
        }

        return ''
    }

    get prinatableDirectorName (): string {
        if ( this.directorId !== null && this.directorMember !== null ) {
            return this.directorMember.reverseFullName
        }

        return this.director ?? ''
    }

    get fullTitle (): string {
        return ts( 'ctl', { ctlId: this.ctlId } ) + ' ' + this.title + ' (' + this.dateRange + ')'
    }

    get dateRange (): string {
        return dateRange( this.from, this.to )
    }

    get orderedClasses (): EventClass[] {
        return this.classes.sort( ( a, b ) => ( a.weight == b.weight ? a.shortcut.localeCompare( b.shortcut ) : b.weight - a.weight ) )
    }

    get orderedCourses (): EventCourse[] {
        return this.courses.sort( ( a, b ) => ( a.title.localeCompare( b.title ) ) )
    }

    get useCourses (): boolean {
        return this.courses.length >= 2
    }

    get isLongDistanceOnly (): boolean {
        return this.classes.length > 0 && this.classes.every( ( cl ) => cl.isLongDistance )
    }

    get orderedClassesList () {
        return this.orderedClasses.map( ( eventClass ) => ( {
            value: eventClass.boatClass.id,
            label: eventClass.eventClassName,
            shortLabel: eventClass.boatClass.shortcut
        } ) )
    }

    get orderedReferees () {
        return this.referees.sort( ( a, b ) => (
            a.refereeRole?.points == b.refereeRole?.points
                ? ( a.refereeRole?.order === b.refereeRole?.order
                        ? ( a.refereeRole?.role === b.refereeRole?.role
                                ? a.reverseFullName.localeCompare( b.reverseFullName )
                                : a.refereeRole?.role.localeCompare( b.refereeRole?.role )
                        )
                        : b.refereeRole?.order - a.refereeRole?.order
                )
                : b.refereeRole?.points - a.refereeRole?.points
        ) )
    }

    get orderedLifeguards () {
        return this.lifeguards.sort( ( a, b ) => (
            a.lifeguardRole?.points == b.lifeguardRole?.points
                ? a.lifeguard?.reverseFullName?.localeCompare( b.lifeguard?.reverseFullName )
                : b.lifeguardRole?.points - a.lifeguardRole?.points )
        )
    }

    filteredBoats ( boatClass = null ): EventBoat[] {
        const eventId = this.id

        if ( this.boats !== undefined )
            return boatClass === null ? this.boats : this.boats.filter( boat => boat.boatClassId === boatClass )

        // Otherwise, fetch only what we really need.
        // We need: boat.boatClass (for eventClassName/useBoatNames), boat.crew (for captain/validity),
        // and minimal EventClass info used by EventBoat getters (boat.eventClass / eventClassName)
        const baseQuery = useRepo( EventBoat )
            .where( 'eventId', eventId )
            .with( 'boatClass' )
            .with( 'crew', ( q ) => {
                q.with( 'member' )
            } )

        return ( boatClass === null )
            ? baseQuery.get()
            : baseQuery.where( 'boatClassId', boatClass ).get()
    }

    orderedBoats ( boatClass = null, ignoreClass = false ) {
        const classesWeights = Object.fromEntries( this.classes.map( eventClass => ( [ eventClass.boatClass?.id ?? eventClass.boatClassId, eventClass.weight ] ) ) )

        return this.filteredBoats( boatClass ).sort( ( a, b ) => (
            ignoreClass
                ? a.sailNoSortable.localeCompare( b.sailNoSortable )
                : (
                    classesWeights[ a.boatClass.id ] == classesWeights[ b.boatClass.id ]
                        ? ( a.boatClass.id === b.boatClass.id
                            ? ( a.sailNoSortable.localeCompare( b.sailNoSortable ) )
                            : a.eventClassName.localeCompare( b.eventClassName ) )
                        : ( classesWeights[ b.boatClass.id ] - classesWeights[ a.boatClass.id ] ) ) ) )
    }

    // Returns races for this event. If boatClass is provided, only races that have results in that class are returned.
    // detail=false returns bare EventRace records (no relations) for performance.
    availableRaces ( boatClass: number | string | null = null, detail: boolean = true ): EventRace[] {
        if ( !detail && boatClass === null )
            return useRepo( EventRace ).where( 'eventId', this.id ).get()

        let races = this.races

        if ( races === null || races === undefined ) {
            const repo = useRepo( EventRace ).where( 'eventId', this.id )

            let query = repo as any

            if ( detail || boatClass !== null ) {
                // If boatClass filter is applied, we always need results and skippedClasses.
                // For results we also need boat to check boatClassId.
                query = query.with( 'results', ( qr: any ) => {
                    qr.with( 'boat' )
                } ).with( 'skippedClasses' )
            }

            races = query.get()
        }

        if ( boatClass !== null ) {
            // Filter races that have at least one result of the requested boat class
            races = races?.filter( ( race: any ) => {
                const hasResult = race.results?.some( ( result: any ) => result.boat?.boatClassId === boatClass )
                const isSkipped = race.skippedClasses?.some( ( skipped: any ) => skipped.boatClassId === boatClass )

                return hasResult && !isSkipped
            } ) ?? []
        }

        return races.sort( ( a: any, b: any ) => ( a.race - b.race ) )
    }

    unfinishedBoats ( race = null, boatClass = null ) {
        const results = race === null ? null : useRepo( EventResult ).where( 'eventRaceId', race.id ).get()
        const finishedBoats = results?.map( result => result.eventBoatId ) ?? []
        const skippedClasses = race?.skippedClasses.map( c => c.boatClassId ) ?? []

        return this.orderedBoats( boatClass, true )
            .filter( boat => !finishedBoats.includes( boat.id ) && !skippedClasses.includes( boat.boatClassId ) )
            .map( ( boat ) => ( {
                value: boat.id,
                label: boat.sailNo
                    + ( ( boat.boatClass.useBoatNames && boat.name !== null && boat.name !== undefined && boat.name.length > 0 ) ? ' ' + boat.name : '' )
                    + ( boatClass === null ? ' (' + boat.eventClassName + ')' : '' )
                    + ( boat.captain !== null ? ' — ' + ts( 'captainShort' ) + ' ' + boat.captain.reverseFullName : '' ),
                searchLabel: boat.sailNo
                    + ( ( boat.boatClass.useBoatNames && boat.name !== null && boat.name !== undefined && boat.name.length > 0 ) ? ' ' + boat.name : '' )
                    + ( boat.captain !== null ? ' ' + boat.captain.reverseFullName : '' ),
                sailNumber: boat.sailNumber
            } ) )
    }

    // Returns EventClass for given boatClass id.
    getClass ( boatClass: number | string ): EventClass | null {
        const eventId = this.id

        if ( this.classes === undefined ) {
            return useRepo( EventClass )
                .where( 'eventId', eventId )
                .where( 'boatClassId', boatClass as any )
                .with( 'boatClass' )
                .with( 'categories' )
                .with( 'event' )
                .first()
        }

        const foundClass = this.classes.filter( cl => cl.boatClassId === ( boatClass as any ) )

        if ( foundClass.length === 0 )
            return null

        return foundClass[ 0 ]
    }

    get venueTitle () {
        return this.venueId != null ? this.venue?.localizedName : this.otherVenue
    }

    get clubTitle () {
        return this.clubId != null ? this.club?.id + ' ' + this.club?.name : this.organizer
    }

    get clubTitleShort () {
        return this.clubId != null ? this.club?.id + ' ' + this.club?.shortcut : this.organizer
    }

    get mainReferee () {
        const eventReferee = useRepo( EventReferee ).where( 'eventId', this.id ).where( 'refereeRoleId', 1 ).with( 'referee' ).first()

        if ( eventReferee === null )
            return null

        return eventReferee
    }

    classAttended ( boatClassId ) {
        return this.classes.find( ( eventClass ) => ( eventClass.boatClassId === boatClassId ) )
    }

    results ( boatClass: number | string | null, categoryId: string | null = null, details: boolean = true, availableRacesList = null, resultsType = 'races' ) {
        if ( boatClass === null )
            return []

        const eventClass = this.getClass( boatClass )

        if ( eventClass === null )
            return []

        if ( availableRacesList === null )
            availableRacesList = this.availableRaces( boatClass, true )

        const eventClassRecord = useRepo( EventClass )
            .where( 'eventId', this.id )
            .where( 'boatClassId', boatClass as any )
            .with( 'boatClass' )
            .with( 'categories' )
            .with( 'event' )
            .first()

        let boats = useRepo( EventBoat )
            .where( 'eventId', this.id )
            .where( 'boatClassId', boatClass as any )
            .with( 'boatClass' )
            .with( 'crew', ( q ) => {
                q.with( 'member', ( qm ) => { qm.with( 'club' ) } )
            } )
            .get()

        if ( categoryId !== null ) {
            const category = useRepo( EventCategory ).with( 'eventClass', ( query ) => {
                query.with( 'event' )
            } ).find( categoryId )

            if ( category !== null )
                boats = boats.filter( ( boat ) => ( category.isBoatInCategory( boat ) ) )
        }

        if ( resultsType === 'bestTime' )
            return this.resultsBestTime( eventClass, eventClassRecord, boats, availableRacesList )

        const discarded = eventClass.discarded
        const totalBoats = eventClass.totalBoats
        const scoringSystem = eventClass.boatClass.scoringSystem            // A or B
        const scoringSystemA = scoringSystem === 'A'

        const pad4 = ( n: number ) => String( n ).padStart( 4, '0' )
        const joinPadded = ( arr: number[] ) => arr.reduce( ( acc, v ) => acc + ' ' + pad4( v ), '' ).trim()
        const joinPaddedReverse = ( arr: number[] ) => arr.reduce( ( acc, v ) => pad4( v ) + ' ' + acc, '' ).trim()

        const buildTieStrings = ( pointsAll: number[], pointsNonDiscarded: number[], pointsDiscarded: number[] ) => {
            const a81 = joinPadded( [ ...pointsNonDiscarded ].sort( ( a, b ) => a - b ) )
            const a82 = joinPaddedReverse( pointsAll )
            const b81 = joinPadded( [ ...pointsDiscarded ].sort( ( a, b ) => a - b ) )
            const b82 = joinPadded( [ ...pointsAll ].sort( ( a, b ) => a - b ) )

            return { a81, a82, b81, b82 }
        }

        const sortBoatAggregates = ( a: any, b: any ) => {
            if ( !a.hasNoValidFinishes && !b.hasNoValidFinishes ) {
                if ( a.points !== b.points )
                    return a.points - b.points

                if ( scoringSystemA ) {
                    if ( a.a81 !== b.a81 )
                        return a.a81 > b.a81 ? 1 : -1

                    return a.a82 > b.a82 ? 1 : -1
                } else {
                    if ( a.b81 !== b.b81 )
                        return a.b81 > b.b81 ? 1 : -1

                    if ( a.b82 !== b.b82 )
                        return a.b82 > b.b82 ? 1 : -1

                    return a.a82 > b.a82 ? 1 : -1
                }
            }

            if ( a.hasNoValidFinishes && b.hasNoValidFinishes )
                return 0

            return a.hasNoValidFinishes ? 1 : -1
        }

        const boatResults = boats.map( ( boat ) => {
            const discardedRaces = boat.discardedRaces( discarded, availableRacesList, totalBoats, eventClassRecord )

            const raceResults = boat
                .cachedResults( availableRacesList, eventClassRecord, totalBoats, details )
                .map( ( res: any ) => {
                    if ( res.details && ( res.details.race === null || res.details.race === undefined ) ) {
                        const race = availableRacesList.find( ( r: any ) => r.id === res.raceId )

                        if ( race ) {
                            if ( !race.results ) {
                                // If race has no results, try to load them if we are in details mode
                                // This shouldn't happen if availableRacesList was loaded with detail:true
                                // but as a safety measure for handicap calculation:
                                race.results = useRepo( EventResult ).where( 'eventRaceId', race.id ).with( 'boat' ).get()
                            }
                            res.details.race = race
                        }
                    }
                    res.isDiscarded = discardedRaces.includes( res.race )
                    return res
                } )

            const nonDiscardedPoints = raceResults.filter( ( r: any ) => !r.isDiscarded ).map( ( r: any ) => r.points )
            const discardedPoints = raceResults.filter( ( r: any ) => r.isDiscarded ).map( ( r: any ) => r.points )
            const allPoints = raceResults.map( ( r: any ) => r.points )

            const pointsSum = raceResults.reduce( ( sum: number, r: any ) => sum + ( r.isDiscarded ? 0 : r.points ), 0 )
            const hasNoValidFinishes = raceResults.every( ( r: any ) => !r.validFinish )

            const { a81, a82, b81, b82 } = buildTieStrings( allPoints, nonDiscardedPoints, discardedPoints )

            return { boat, results: raceResults, points: pointsSum, hasNoValidFinishes, a81, a82, b81, b82 }
        } ).sort( sortBoatAggregates )

        let lastDistinguisher = ''
        let index = 0

        boatResults.forEach( ( item: any ) => {
            const distinguisher = item.points + '|' + item.a81 + '|' + item.a82

            if ( distinguisher !== lastDistinguisher ) {
                ++index
                lastDistinguisher = distinguisher
            }

            item.index = index
        } )

        const totalRacedBoats = eventClass.cachedTotalRacedBoats( availableRacesList )
        const calculatedWeight = eventClass.cacheCalculatedWeight( availableRacesList )

        boatResults.forEach( ( item: any ) => {
            item.logPoints = item.hasNoValidFinishes ? 0 : Math.round( ( Math.log10( totalRacedBoats / item.index ) * 1000 + 101 ) * calculatedWeight )
        } )

        return boatResults
    }

    resultsBestTime ( eventClass, eventClassRecord, boats, availableRacesList ) {
        const totalBoats = eventClass.totalBoats

        // 1. For each race, find the worst real time
        const raceWorstTimes = new Map<number, number>()
        const validRaces = new Set<number>()

        availableRacesList.forEach( ( race: any ) => {
            let maxTime = 0
            let hasAnyTime = false

            boats.forEach( ( boat: any ) => {
                const res = useRepo( EventResult )
                    .where( 'eventRaceId', race.id )
                    .where( 'eventBoatId', boat.id )
                    .with( 'race' )
                    .with( 'boat' )
                    .first()
                if ( res ) {
                    const time = res.realTime()
                    if ( time !== null && res.other === null ) {
                        maxTime = Math.max( maxTime, time )
                        hasAnyTime = true
                    }
                }
            } )

            if ( hasAnyTime ) {
                raceWorstTimes.set( race.race, maxTime )
                validRaces.add( race.race )
            }
        } )

        // 2. Calculate total time for each boat
        const boatResults = boats.map( ( boat ) => {
            let totalTime = 0
            const raceResults: any[] = []

            availableRacesList.forEach( ( race: any ) => {
                if ( !validRaces.has( race.race ) )
                    return

                const res = useRepo( EventResult )
                    .where( 'eventRaceId', race.id )
                    .where( 'eventBoatId', boat.id )
                    .with( 'race' )
                    .with( 'boat' )
                    .first()

                let boatRaceTime = 0
                let validFinish = false
                let resultText: string | number = ''

                if ( res ) {
                    const time = res.realTime()

                    if ( time !== null ) {
                        boatRaceTime = time
                        validFinish = true
                        resultText = res.other === null ? ds( time, DateFormat.Time, true ) : res.other
                    }
                    else {
                        boatRaceTime = ( raceWorstTimes.get( race.race ) ?? 0 ) + ( ( eventClass.racePenaltyTime ?? 10 ) * 60 )
                        resultText = res.other ?? ''
                    }
                } else {
                    boatRaceTime = ( raceWorstTimes.get( race.race ) ?? 0 ) + ( ( eventClass.racePenaltyTime ?? 10 ) * 60 )
                    resultText = ''
                }

                totalTime += boatRaceTime

                raceResults.push( {
                    race: race.race,
                    points: boatRaceTime,
                    result: resultText,
                    validFinish,
                    details: res ?? {
                        race: race,
                        boat: boat,
                        eventRaceId: race.id,
                        eventBoatId: boat.id,
                        other: res?.other ?? ( validFinish ? null : 'DNC' ),
                        isHandicap: false,
                        realTime: () => boatRaceTime,
                        calculatedTime: () => boatRaceTime,
                        orderInclHandicap: null,
                        finishTime: null
                    }
                } )
            } )

            return {
                boat,
                results: raceResults,
                points: totalTime,
                pointsText: ds( totalTime, DateFormat.Time, true ),
                hasNoValidFinishes: raceResults.every( r => !r.validFinish )
            }
        } )

        // 3. Sort by total time
        boatResults.sort( ( a, b ) => {
            if ( a.hasNoValidFinishes && b.hasNoValidFinishes ) return 0
            if ( a.hasNoValidFinishes ) return 1
            if ( b.hasNoValidFinishes ) return -1
            return a.points - b.points
        } )

        // 4. Assign indices and logPoints
        let lastPoints = -1
        let index = 0
        boatResults.forEach( ( item, i ) => {
            if ( item.points !== lastPoints ) {
                index = i + 1
                lastPoints = item.points
            }
            item.index = index
        } )

        const totalRacedBoats = eventClass.cachedTotalRacedBoats( availableRacesList )
        const calculatedWeight = eventClass.cacheCalculatedWeight( availableRacesList )

        boatResults.forEach( ( item: any ) => {
            item.logPoints = item.hasNoValidFinishes ? 0 : Math.round( ( Math.log10( totalRacedBoats / item.index ) * 1000 + 101 ) * calculatedWeight )
        } )

        return boatResults
    }

    allBoatsOrdered ( boatClass = null ) {
        return this.filteredBoats( ( boatClass === null || boatClass === '-all' ) ? null : boatClass )
            .map( boat => {
                const b: any = { ...boat, sailNo: boat.sailNo, finished: false }

                b.boatClass = boat.boatClass
                b.crew = boat.crew

                return b
            } )
            .sort( ( a, b ) => ( a.sailNumber - b.sailNumber ) )
    }

    setModified ( value: boolean = true ) {
        useRepo( EventsRepo ).save( { id: this.id, modified: value } )
    }

    static piniaOptions = {
        persist: false
    }
}
